/**
 * Post-call prompt for unknown caller lead creation.
 * Shows after an inbound call ends where no lead was linked.
 * Compact card with quick-create form (name required, phone pre-filled).
 */

import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { UserPlus, X, Check } from 'lucide-react'
import { voipApi } from '../../api/endpoints'

interface PostCallLeadPromptProps {
  callerNumber: string
  voipCallId: number
  onCreated: (leadId: number, leadName: string) => void
  onDismiss: () => void
}

export default function PostCallLeadPrompt({
  callerNumber,
  voipCallId,
  onCreated,
  onDismiss,
}: PostCallLeadPromptProps) {
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [company, setCompany] = useState('')
  const [email, setEmail] = useState('')
  const [created, setCreated] = useState<{ leadName: string; isdup: boolean; linkedCount: number } | null>(null)

  const createMutation = useMutation({
    mutationFn: () =>
      voipApi.createLeadFromCall(voipCallId, {
        name,
        ...(company ? { company } : {}),
        ...(email ? { email } : {}),
      }),
    onSuccess: (result) => {
      setCreated({ leadName: result.lead_name, isdup: result.is_duplicate, linkedCount: result.linked_count })
      queryClient.invalidateQueries({ queryKey: ['voip-calls'] })
      onCreated(result.lead_id, result.lead_name)
    },
  })

  if (created) {
    return (
      <div className="rounded-xl border border-green-200 bg-green-50 p-4">
        <div className="flex items-center gap-2">
          <Check size={16} className="text-green-600" />
          <div>
            <span className="text-sm font-medium text-green-700">
              {created.isdup ? `Linked to existing lead: ${created.leadName}` : `Lead created: ${created.leadName}`}
            </span>
            {created.linkedCount > 1 && (
              <p className="text-xs text-green-600 mt-0.5">
                {created.linkedCount} call records linked to this lead (including previous calls from this number)
              </p>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <UserPlus size={16} className="text-amber-600" />
          <span className="text-sm font-semibold text-amber-800">Unknown Caller — Create Lead?</span>
        </div>
        <button onClick={onDismiss} className="p-1 text-amber-400 hover:text-amber-600 rounded">
          <X size={14} />
        </button>
      </div>

      <p className="text-xs text-amber-600 mb-3">
        Phone: <span className="font-mono font-medium">{callerNumber}</span>
      </p>

      <div className="space-y-2">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Name (required)"
          className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm outline-none focus:ring-1 focus:ring-indigo-500"
        />
        <div className="grid grid-cols-2 gap-2">
          <input
            type="text"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            placeholder="Company"
            className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm outline-none focus:ring-1 focus:ring-indigo-500"
          />
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm outline-none focus:ring-1 focus:ring-indigo-500"
          />
        </div>
      </div>

      <div className="flex items-center gap-2 mt-3">
        <button
          onClick={onDismiss}
          className="flex-1 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
        >
          Dismiss
        </button>
        <button
          onClick={() => createMutation.mutate()}
          disabled={!name.trim() || createMutation.isPending}
          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
        >
          {createMutation.isPending ? 'Creating...' : 'Create Lead'}
        </button>
      </div>

      {createMutation.error && (
        <p className="text-xs text-red-600 mt-2">{(createMutation.error as Error).message}</p>
      )}
    </div>
  )
}
